package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** Stable task kinds. Human-readable copy stays in client string resources. */
@Serializable
enum class DailyTaskType {
    @SerialName("message") MESSAGE,
    @SerialName("discover_post") DISCOVER_POST,
    @SerialName("discover_like") DISCOVER_LIKE,
    @SerialName("steps") STEPS,
    @SerialName("discover_save") DISCOVER_SAVE,
    @SerialName("reply") REPLY,
    @SerialName("picso_post") PICSO_POST,
    @SerialName("picso_react") PICSO_REACT,
    @SerialName("picso_save") PICSO_SAVE,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
enum class DailyTaskStatus {
    @SerialName("active") ACTIVE,
    @SerialName("completed") COMPLETED,
    @SerialName("replaced") REPLACED,
    @SerialName("expired") EXPIRED,
    @SerialName("cancelled") CANCELLED,
    @SerialName("unknown") UNKNOWN,
}

/** Existing app surface a task may open. This is navigation, never automatic action. */
@Serializable
enum class DailyTaskAction {
    @SerialName("chats") CHATS,
    @SerialName("discover") DISCOVER,
    @SerialName("step_permission") STEP_PERMISSION,
    @SerialName("none") NONE,
    @SerialName("picso") PICSO,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
data class DailyTask(
    @SerialName("id") val id: Uuid,
    @SerialName("code") val code: String,
    @SerialName("type") val type: DailyTaskType = DailyTaskType.UNKNOWN,
    @SerialName("action") val action: DailyTaskAction = DailyTaskAction.UNKNOWN,
    @SerialName("goal") val goal: Int,
    @SerialName("progress") val progress: Int,
    @SerialName("xp_reward") val xpReward: Int,
    @SerialName("status") val status: DailyTaskStatus = DailyTaskStatus.UNKNOWN,
    @SerialName("completed_at") val completedAt: Timestamp? = null,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
data class DailyTaskSet(
    @SerialName("id") val id: Uuid,
    /** ISO 8601 calendar date in [timezone], not an instant. */
    @SerialName("local_date") val localDate: String,
    @SerialName("timezone") val timezone: String,
    @SerialName("starts_at") val startsAt: Timestamp,
    @SerialName("expires_at") val expiresAt: Timestamp,
    @SerialName("tasks") val tasks: List<DailyTask>,
    @SerialName("completed_count") val completedCount: Int,
    @SerialName("total_count") val totalCount: Int,
    @SerialName("earned_xp") val earnedXp: Int,
    @SerialName("bonus_xp") val bonusXp: Int,
    @SerialName("replacement_available") val replacementAvailable: Boolean,
    @SerialName("completed_at") val completedAt: Timestamp? = null,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
data class DailyTaskSetPage(
    @SerialName("items") val items: List<DailyTaskSet>,
    @SerialName("page_info") val pageInfo: PageInfo,
)

@Serializable
data class TaskProfile(
    @SerialName("timezone") val timezone: String,
    @SerialName("total_xp") val totalXp: Int,
    @SerialName("spendable_xp") val spendableXp: Int = 0,
    @SerialName("streak_freezes") val streakFreezes: Int = 0,
    @SerialName("level") val level: Int,
    @SerialName("current_streak") val currentStreak: Int,
    @SerialName("longest_streak") val longestStreak: Int,
    @SerialName("step_tasks_enabled") val stepTasksEnabled: Boolean,
    @SerialName("notifications_enabled") val notificationsEnabled: Boolean,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
data class ReplaceDailyTaskRequest(
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("task_id") val taskId: Uuid,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
enum class StepTaskUnavailableReason {
    @SerialName("permission_denied") PERMISSION_DENIED,
    @SerialName("permission_revoked") PERMISSION_REVOKED,
    @SerialName("provider_unavailable") PROVIDER_UNAVAILABLE,
    @SerialName("unknown") UNKNOWN,
}

/**
 * Replaces an unusable active step task without consuming the user's ordinary daily replacement.
 * The reason is diagnostic only and never grants progress or XP.
 */
@Serializable
data class ReplaceUnavailableStepTaskRequest(
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("task_id") val taskId: Uuid,
    @SerialName("reason") val reason: StepTaskUnavailableReason = StepTaskUnavailableReason.UNKNOWN,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
data class UpdateTaskPreferencesRequest(
    @SerialName("timezone") val timezone: String,
    @SerialName("step_tasks_enabled") val stepTasksEnabled: Boolean,
    @SerialName("notifications_enabled") val notificationsEnabled: Boolean,
    @SerialName("version") val version: ResourceVersion,
)

@Serializable
enum class TaskStepSource {
    @SerialName("health_connect") HEALTH_CONNECT,
    @SerialName("step_counter") STEP_COUNTER,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
data class ReportTaskStepsRequest(
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("local_date") val localDate: String,
    @SerialName("steps") val steps: Int,
    @SerialName("source") val source: TaskStepSource = TaskStepSource.UNKNOWN,
    @SerialName("measured_at") val measuredAt: Timestamp,
)
